export type VideoTextItem = {
  id: string;
  text: string;
  startTime: number;
  endTime: number;
  position: string;
};

export interface VideoGenerationParams {
  videoProvider: string;
  videoPrompt?: string;
  videoDuration: number;
  videoRestrictions?: string;
  logoFile?: File | null;
  referenceImages?: File[];
  videoTexts?: VideoTextItem[];
  videoScript?: string;
}

function baseFields(params: VideoGenerationParams): Record<string, unknown> {
  const fields: Record<string, unknown> = {
    duration: params.videoDuration,
    model:
      params.videoProvider === "heygen"
        ? "heygen-video"
        : "veo-3.1-generate-preview",
    style: "cinematic",
    resolution: "1080p",
    provider: params.videoProvider,
  };

  if (params.videoPrompt?.trim()) {
    fields.prompt = params.videoPrompt.trim();
  }
  if (params.videoRestrictions?.trim()) {
    fields.restrictions = params.videoRestrictions.trim();
  }
  if ((params.videoTexts?.length ?? 0) > 0) {
    fields.video_texts = params.videoTexts;
  }
  if (params.videoScript?.trim()) {
    fields.video_script = params.videoScript.trim();
  }

  return fields;
}

export function shouldUseMultipartVideoRequest(
  params: VideoGenerationParams,
): boolean {
  const supportsAssets =
    params.videoProvider === "heygen" || params.videoProvider === "veo";
  const hasFiles =
    supportsAssets &&
    (!!params.logoFile || (params.referenceImages?.length ?? 0) > 0);
  const hasContent =
    (params.videoTexts?.length ?? 0) > 0 || !!params.videoScript?.trim();
  return hasFiles || hasContent;
}

export function buildVideoGenerationRequest(
  params: VideoGenerationParams,
): FormData | Record<string, unknown> {
  const fields = baseFields(params);

  if (!shouldUseMultipartVideoRequest(params)) {
    return fields;
  }

  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (key === "video_texts") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });

  const supportsAssets =
    params.videoProvider === "heygen" || params.videoProvider === "veo";
  if (supportsAssets && params.logoFile) {
    formData.append("logo", params.logoFile);
  }
  if (supportsAssets && params.referenceImages) {
    params.referenceImages.forEach((img) => {
      formData.append("reference_images", img);
    });
  }

  return formData;
}
